import { animated, useSpring } from '@react-spring/web';
import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import * as React from 'react';
import { Controller, FieldError, FieldErrorsImpl, Merge, useFormContext } from 'react-hook-form';
import { ISatisfactionSurvey } from '../../../../../extViewmodels';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../../models';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { StarRating } from '../../../../components/surveys/StarRating';
import { XIcon } from '../../../../components/svgs/icons/XIcon';
import { Input } from '../../../../components-v2/Input';
import { TextInputFormField } from '../../../../components/TextInputFormField';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { baseStyleSheet, bs } from '../../../../styles/styles';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { useNotificationOptions } from '../../shared/hooks/useNotificationOptions';
import { styleSheet } from '../styles';
import { STAR_RATING_LIMIT } from '../utils';

const LIMIT = STAR_RATING_LIMIT;

const ErrorMessage = ({ error }: { error: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>> }) => {
	if (!error) {
		return null;
	}
	return <p className={css(styleSheet.errors)}>{error as React.ReactNode}</p>;
};

export const NotificationOptionsBase: React.FC<
	IImpersonationContextComponentProps & { survey: ISatisfactionSurvey }
> = ({ survey }) => {
	const formContext = useFormContext<ISatisfactionSurvey>();

	const {
		formValues,
		recipients,
		isNotificationEnabled,
		hasAdditionalRecipients,
		shouldShowCreatorEmail,
		addRecipient,
		removeRecipient,
		updateRecipient,
	} = useNotificationOptions({
		formContext: formContext as any,
		isArchived: !!survey?.archivedDate,
		creatorEmail: survey?.creator?.email,
	});

	const onNotificationStarRatingOptionCheckChanged = React.useCallback(
		(value: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const indexOfMatch = formValues?.notificationSettings?.ratings?.findIndex((x: number) => x === value);

			if (indexOfMatch > -1 && !e.target.checked) {
				const nextRatings: number[] = (formValues?.notificationSettings?.ratings || []).slice();
				nextRatings.splice(indexOfMatch, 1);
				nextRatings.sort((a, b) => a - b);

				formContext.reset(values => ({
					...values,
					notificationSettings: {
						...values.notificationSettings,
						ratings: nextRatings,
					},
				}));
				// CRITICAL: Preserve exact functionality - if no star ratings are selected, set the ratings to 1,2,3,4,5
				if (nextRatings.length === 0) {
					formContext.reset(values => ({
						...values,
						notificationSettings: {
							...values.notificationSettings,
							ratings: [1, 2, 3, 4, 5],
						},
					}));
				}
				return;
			}
			if (e.target.checked) {
				const nextRatings: number[] = (formValues?.notificationSettings?.ratings || []).slice();
				nextRatings.push(value);
				nextRatings.sort((a, b) => a - b);
				formContext.reset(values => ({
					...values,
					notificationSettings: {
						...values.notificationSettings,
						ratings: nextRatings,
					},
				}));
			}
		},
		[formValues.notificationSettings.ratings, formContext]
	);

	const animationStyles = useSpring({
		from: {
			height: 0,
			opacity: 0,
		},
		to: {
			height: isNotificationEnabled ? 300 : 0,
			opacity: isNotificationEnabled ? 1 : 0,
		},
	});
	return (
		<section className={css(styleSheet.section, styleSheet.notificationSection)}>
			<h3 className={css(styleSheet.sectionTitle)}>Notification Options</h3>

			<FormFieldWrapper
				name='notificationSettings.isEnabled'
				control={formContext.control}
				title='Receive an email notification for each survey response.'
				description='Get notified via email when someone completes your satisfaction survey.'
			>
				{({ value, onChange }) => (
					<Toggle
						id='edit-survey-notification-enabled-toggle'
						isOn={value}
						onToggleCheckChanged={onChange}
						uncheckedColor={grayIconFill}
					/>
				)}
			</FormFieldWrapper>
			<animated.div style={animationStyles}>
				<fieldset
					className={css(
						styleSheet.fieldset,
						baseStyleSheet.verticalStack,
						isNotificationEnabled ? styleSheet.notificationSettingsEnabled : styleSheet.notificationSettingsDisabled
					)}
					disabled={!isNotificationEnabled}
				>
					<div className={css(styleSheet.fieldGroup)}>
						{Array.from({ length: LIMIT }).map((_, i) => {
							const count = LIMIT - i;
							return (
								<Controller
									key={`edit-survey-notification-${count}stars`}
									name='notificationSettings.ratings'
									control={formContext.control}
									render={({ field: { ref, value, ...fieldProps } }) => {
										fieldProps.onChange = onNotificationStarRatingOptionCheckChanged(count);
										const isChecked = formValues?.notificationSettings?.ratings?.indexOf(count) > -1;
										return (
											<Checkbox
												checked={isChecked}
												disabled={!!survey?.archivedDate}
												id={`edit-survey-notification-${count}stars`}
												{...fieldProps}
											>
												<div className={css(styleSheet.starRating)}>
													<span>{`${count}/${LIMIT}`}</span>
													<StarRating fillColor={THEME.colors.pill.yellow.icon} readonly total={LIMIT} value={count} />
												</div>
											</Checkbox>
										);
									}}
								/>
							);
						})}
					</div>
					<div className={css(styleSheet.additionalRecipientsCreator)}>
						{shouldShowCreatorEmail && (
							<div className={css(styleSheet.constrainedFieldGroup)}>
								<label htmlFor='edit-survey-notification-email'>Survey Creator</label>
								<div className={css(styleSheet.notificationEmail)}>
									<Input
										autoComplete='off'
										type='text'
										id='edit-survey-notification-email'
										value={survey?.creator?.email}
										disabled
									/>
								</div>
							</div>
						)}
					</div>

					<div className={css(styleSheet.constrainedFieldGroup)}>
						{isNotificationEnabled && hasAdditionalRecipients && (
							<label htmlFor='edit-survey-notification-email' className={css(styleSheet.additionalRecipientsLabel)}>
								Additional Recipients
							</label>
						)}
						<Controller
							name='notificationSettings.additionalRecipients'
							control={formContext.control}
							render={({ field: { ref, ..._fieldProps } }) => {
								return (
									<>
										{recipients.map((_recipient: string, index: number) => {
											const value = recipients[index];
											return (
												<div
													key={`edit-survey-notification-additional-recipient-${index}`}
													className={css(styleSheet.additionalRecipient)}
												>
													<div className={css(bs.flex, bs.gap2, bs.itemsCenter)}>
														<div>
															<TextInputFormField
																type={'text'}
																value={value}
																onChange={e => {
																	updateRecipient(index, e.target.value);
																}}
															/>
														</div>
														<div>
															<Button
																type='button'
																kind='custom'
																styles={[styleSheet.removeButton]}
																onClick={() => {
																	removeRecipient(index);
																}}
															>
																<span>Remove</span>
																<XIcon className={css(styleSheet.removeButtonIcon)} />
															</Button>
														</div>
													</div>
													<ErrorMessage
														error={formContext.formState.errors?.notificationSettings?.additionalRecipients?.message}
													/>
												</div>
											);
										})}
										<Button type='button' kind='link' onClick={addRecipient}>
											+ Add Email
										</Button>
									</>
								);
							}}
						/>
					</div>
				</fieldset>
			</animated.div>
		</section>
	);
};

export const NotificationOptions = inject(ImpersonationContextKey)(NotificationOptionsBase);
